/**
 * The geometry under art markup, pure so it can be tested without a GPU: an affine matrix, path
 * flattening, triangulation, hit testing, dashes, trims and stroke outlines.
 *
 * The renderer has no rotation and no paths, only quads evaluated as distance fields. So anything
 * an author can turn, skew or bend is turned into points here, on the CPU, and handed to the
 * renderer as triangles - which is what makes a hexagonal button or a sigil turning on its axis
 * possible without a second pipeline.
 */
import type { Point } from "../../../common/geometry.js";

// --- matrices -----------------------------------------------------------------------------------

/** `[a, b, c, d, e, f]`: x' = a·x + c·y + e, y' = b·x + d·y + f - SVG's order. */
export type Matrix = readonly [number, number, number, number, number, number];

export const IDENTITY: Matrix = [1, 0, 0, 1, 0, 0];

/** `m` then `n`: the matrix that applies `n` first and `m` after. */
export function multiply(m: Matrix, n: Matrix): Matrix {
  return [
    m[0] * n[0] + m[2] * n[1],
    m[1] * n[0] + m[3] * n[1],
    m[0] * n[2] + m[2] * n[3],
    m[1] * n[2] + m[3] * n[3],
    m[0] * n[4] + m[2] * n[5] + m[4],
    m[1] * n[4] + m[3] * n[5] + m[5],
  ];
}

export const translation = (x: number, y: number): Matrix => [1, 0, 0, 1, x, y];
export const scaling = (sx: number, sy = sx): Matrix => [sx, 0, 0, sy, 0, 0];

/** Clockwise on screen by `degrees`, about the origin. */
export function rotation(degrees: number): Matrix {
  const r = (degrees * Math.PI) / 180;
  const c = Math.cos(r);
  const s = Math.sin(r);
  return [c, s, -s, c, 0, 0];
}

export function apply(m: Matrix, p: Point): Point {
  return { x: m[0] * p.x + m[2] * p.y + m[4], y: m[1] * p.x + m[3] * p.y + m[5] };
}

export function applyAll(m: Matrix, points: readonly Point[]): Point[] {
  return points.map((p) => apply(m, p));
}

/** How much a length grows under `m`, on average across its two axes. */
export function scaleOf(m: Matrix): number {
  return Math.sqrt(Math.abs(m[0] * m[3] - m[1] * m[2]));
}

/** True when `m` keeps a circle a circle: uniform scale, any rotation, no skew. */
export function isConformal(m: Matrix): boolean {
  const sx = Math.hypot(m[0], m[1]);
  const sy = Math.hypot(m[2], m[3]);
  const dot = m[0] * m[2] + m[1] * m[3];
  return Math.abs(sx - sy) <= 1e-6 * Math.max(sx, sy, 1) && Math.abs(dot) <= 1e-6 * Math.max(sx * sy, 1);
}

/** True when `m` keeps an axis-aligned rect axis-aligned and the same way round. */
export function isAxisAligned(m: Matrix): boolean {
  return Math.abs(m[1]) < 1e-9 && Math.abs(m[2]) < 1e-9 && m[0] > 0 && m[3] > 0;
}

/** The rotation `m` applies, in radians clockwise. */
export function angleOf(m: Matrix): number {
  return Math.atan2(m[1], m[0]);
}

// --- outlines -----------------------------------------------------------------------------------

/** One run of a path: its points, and whether its end joins its start. */
export interface Subpath {
  points: Point[];
  closed: boolean;
}

/** How many segments a curve of `radius` sweeping `sweep` radians is drawn in. */
export function segmentsFor(radius: number, sweep: number, tolerance = 0.25): number {
  if (radius <= tolerance) return Math.max(3, Math.ceil(Math.abs(sweep) / (Math.PI / 2)));
  const step = 2 * Math.acos(Math.max(-1, Math.min(1, 1 - tolerance / radius)));
  return Math.min(512, Math.max(3, Math.ceil(Math.abs(sweep) / Math.max(step, 1e-3))));
}

/** An ellipse's outline, clockwise from three o'clock. */
export function ellipsePoints(cx: number, cy: number, rx: number, ry: number, tolerance = 0.25): Point[] {
  const n = segmentsFor(Math.max(rx, ry), Math.PI * 2, tolerance);
  return Array.from({ length: n }, (_, i) => {
    const a = (i / n) * Math.PI * 2;
    return { x: cx + Math.cos(a) * rx, y: cy + Math.sin(a) * ry };
  });
}

/**
 * Points along a circular arc, angles in radians clockwise from twelve o'clock - the convention
 * `fillArc` uses, so an `<Arc>` drawn through either path agrees.
 */
export function arcPoints(cx: number, cy: number, r: number, start: number, end: number, tolerance = 0.25): Point[] {
  const n = segmentsFor(r, end - start, tolerance);
  return Array.from({ length: n + 1 }, (_, i) => {
    const a = start + ((end - start) * i) / n;
    return { x: cx + Math.sin(a) * r, y: cy - Math.cos(a) * r };
  });
}

/** A rect's outline, its corners rounded by `rx` (and `ry`). */
export function rectPoints(x: number, y: number, w: number, h: number, rx = 0, ry = rx, tolerance = 0.25): Point[] {
  const r1 = Math.max(0, Math.min(rx, w / 2));
  const r2 = Math.max(0, Math.min(ry, h / 2));
  if (r1 <= 0 || r2 <= 0) {
    return [
      { x, y },
      { x: x + w, y },
      { x: x + w, y: y + h },
      { x, y: y + h },
    ];
  }
  const out: Point[] = [];
  const corner = (cx: number, cy: number, from: number) => {
    const n = Math.max(2, Math.ceil(segmentsFor(Math.max(r1, r2), Math.PI / 2, tolerance)));
    for (let i = 0; i <= n; i++) {
      const a = from + (Math.PI / 2) * (i / n);
      out.push({ x: cx + Math.cos(a) * r1, y: cy + Math.sin(a) * r2 });
    }
  };
  corner(x + w - r1, y + r2, -Math.PI / 2);
  corner(x + w - r1, y + h - r2, 0);
  corner(x + r1, y + h - r2, Math.PI / 2);
  corner(x + r1, y + r2, Math.PI);
  return out;
}

/** The vertices of a regular polygon (`inner` absent) or a star (`inner` the trough radius), from twelve o'clock. */
export function starPoints(cx: number, cy: number, points: number, r: number, inner?: number, turn = 0): Point[] {
  const n = Math.max(2, Math.floor(points));
  const out: Point[] = [];
  const count = inner === undefined ? n : n * 2;
  for (let i = 0; i < count; i++) {
    const radius = inner === undefined || i % 2 === 0 ? r : inner;
    const a = (i / count) * Math.PI * 2 + turn;
    out.push({ x: cx + Math.sin(a) * radius, y: cy - Math.cos(a) * radius });
  }
  return out;
}

/**
 * The strokes of a star polygon {n/k} as separate subpaths, in drawing order: vertex j to j + k.
 * Separate because a `reveal` draws a star stroke by stroke, and a star of seven points and step
 * three is one closed loop that a trim would otherwise draw as one long line.
 */
export function starStrokePaths(cx: number, cy: number, points: number, step: number, r: number, turn = 0): Subpath[] {
  const n = Math.max(3, Math.floor(points));
  const k = Math.max(1, Math.floor(step)) % n || 1;
  const v = starPoints(cx, cy, n, r, undefined, turn);
  const out: Subpath[] = [];
  const seen = new Set<string>();
  for (let start = 0; start < n && out.length < n; start++) {
    let at = start;
    for (let i = 0; i < n; i++) {
      const next = (at + k) % n;
      const key = at < next ? `${at}-${next}` : `${next}-${at}`;
      if (seen.has(key)) break;
      seen.add(key);
      out.push({ points: [v[at]!, v[next]!], closed: false });
      at = next;
    }
  }
  return out;
}

// --- path data -----------------------------------------------------------------------------------

export class PathError extends Error {}

/**
 * SVG path data to subpaths: M, L, H, V, C, S, Q, T, A and Z, upper case absolute and lower case
 * relative. Curves are flattened to within `tolerance` of the true curve.
 */
export function flattenPath(d: string, tolerance = 0.25): Subpath[] {
  const tokens = d.match(/[MLHVCSQTAZmlhvcsqtaz]|[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/g) ?? [];
  const leftover = d.replace(/[MLHVCSQTAZmlhvcsqtaz]|[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?|[\s,]/g, "");
  if (leftover.length > 0) throw new PathError(`path data cannot contain "${leftover[0]}"`);
  const out: Subpath[] = [];
  let current: Subpath | null = null;
  let x = 0;
  let y = 0;
  let sx = 0;
  let sy = 0;
  /** The last control point, for S and T. */
  let cx = 0;
  let cy = 0;
  let last = "";
  let i = 0;
  let command = "";

  const number = (): number => {
    const t = tokens[i];
    if (t === undefined || /[A-Za-z]/.test(t)) throw new PathError(`"${command}" needs more numbers`);
    i++;
    return Number(t);
  };
  const lineTo = (nx: number, ny: number) => {
    if (!current) {
      current = { points: [{ x, y }], closed: false };
      out.push(current);
    }
    current.points.push({ x: nx, y: ny });
    x = nx;
    y = ny;
  };
  const curve = (pts: (t: number) => Point, length: number) => {
    const n = Math.min(256, Math.max(2, Math.ceil(Math.sqrt(length / Math.max(tolerance, 1e-3)) * 1.2)));
    for (let k = 1; k <= n; k++) {
      const p = pts(k / n);
      lineTo(p.x, p.y);
    }
  };

  while (i < tokens.length) {
    const t = tokens[i]!;
    if (/[A-Za-z]/.test(t)) {
      command = t;
      i++;
    } else if (!command) {
      throw new PathError("path data must start with a command");
    }
    const rel = command === command.toLowerCase();
    const ox = rel ? x : 0;
    const oy = rel ? y : 0;
    switch (command.toUpperCase()) {
      case "M": {
        const nx = number() + ox;
        const ny = number() + oy;
        current = { points: [{ x: nx, y: ny }], closed: false };
        out.push(current);
        x = sx = nx;
        y = sy = ny;
        // Further pairs after a move are lines.
        command = rel ? "l" : "L";
        break;
      }
      case "L":
        lineTo(number() + ox, number() + oy);
        break;
      case "H":
        lineTo(number() + ox, y);
        break;
      case "V":
        lineTo(x, number() + oy);
        break;
      case "C":
      case "S": {
        let x1: number;
        let y1: number;
        if (command.toUpperCase() === "C") {
          x1 = number() + ox;
          y1 = number() + oy;
        } else {
          const smooth = /[CS]/i.test(last);
          x1 = smooth ? 2 * x - cx : x;
          y1 = smooth ? 2 * y - cy : y;
        }
        const x2 = number() + ox;
        const y2 = number() + oy;
        const x3 = number() + ox;
        const y3 = number() + oy;
        const x0 = x;
        const y0 = y;
        const length = Math.hypot(x1 - x0, y1 - y0) + Math.hypot(x2 - x1, y2 - y1) + Math.hypot(x3 - x2, y3 - y2);
        curve((u) => {
          const v = 1 - u;
          return {
            x: v * v * v * x0 + 3 * v * v * u * x1 + 3 * v * u * u * x2 + u * u * u * x3,
            y: v * v * v * y0 + 3 * v * v * u * y1 + 3 * v * u * u * y2 + u * u * u * y3,
          };
        }, length);
        cx = x2;
        cy = y2;
        break;
      }
      case "Q":
      case "T": {
        let x1: number;
        let y1: number;
        if (command.toUpperCase() === "Q") {
          x1 = number() + ox;
          y1 = number() + oy;
        } else {
          const smooth = /[QT]/i.test(last);
          x1 = smooth ? 2 * x - cx : x;
          y1 = smooth ? 2 * y - cy : y;
        }
        const x2 = number() + ox;
        const y2 = number() + oy;
        const x0 = x;
        const y0 = y;
        curve((u) => {
          const v = 1 - u;
          return { x: v * v * x0 + 2 * v * u * x1 + u * u * x2, y: v * v * y0 + 2 * v * u * y1 + u * u * y2 };
        }, Math.hypot(x1 - x0, y1 - y0) + Math.hypot(x2 - x1, y2 - y1));
        cx = x1;
        cy = y1;
        break;
      }
      case "A": {
        const rx = Math.abs(number());
        const ry = Math.abs(number());
        const phi = (number() * Math.PI) / 180;
        const large = number() !== 0;
        const sweep = number() !== 0;
        const x2 = number() + ox;
        const y2 = number() + oy;
        arcTo(x, y, rx, ry, phi, large, sweep, x2, y2, tolerance).forEach((p) => lineTo(p.x, p.y));
        x = x2;
        y = y2;
        break;
      }
      case "Z":
        if (current) {
          current.closed = true;
          x = sx;
          y = sy;
          current = null;
        }
        break;
      default:
        throw new PathError(`"${command}" is not a path command`);
    }
    if (!/[CSQT]/i.test(command)) {
      cx = x;
      cy = y;
    }
    last = command;
  }
  // Drop the closing point a Z would double, and the subpaths that are only a move.
  for (const s of out) {
    const p = s.points;
    if (s.closed && p.length > 1 && near(p[0]!, p[p.length - 1]!)) p.pop();
  }
  return out.filter((s) => s.points.length > 1);
}

/** SVG's endpoint-parameterised elliptical arc, as points after the start. */
function arcTo(
  x1: number, y1: number, rx: number, ry: number, phi: number,
  large: boolean, sweep: boolean, x2: number, y2: number, tolerance: number,
): Point[] {
  if (rx === 0 || ry === 0 || (x1 === x2 && y1 === y2)) return [{ x: x2, y: y2 }];
  const cos = Math.cos(phi);
  const sin = Math.sin(phi);
  const dx = (x1 - x2) / 2;
  const dy = (y1 - y2) / 2;
  const x1p = cos * dx + sin * dy;
  const y1p = -sin * dx + cos * dy;
  const lambda = (x1p * x1p) / (rx * rx) + (y1p * y1p) / (ry * ry);
  if (lambda > 1) {
    rx *= Math.sqrt(lambda);
    ry *= Math.sqrt(lambda);
  }
  const num = rx * rx * ry * ry - rx * rx * y1p * y1p - ry * ry * x1p * x1p;
  const den = rx * rx * y1p * y1p + ry * ry * x1p * x1p;
  const k = (large === sweep ? -1 : 1) * Math.sqrt(Math.max(0, num / den));
  const cxp = (k * rx * y1p) / ry;
  const cyp = (-k * ry * x1p) / rx;
  const cx = cos * cxp - sin * cyp + (x1 + x2) / 2;
  const cy = sin * cxp + cos * cyp + (y1 + y2) / 2;
  const angle = (ux: number, uy: number, vx: number, vy: number) =>
    Math.atan2(ux * vy - uy * vx, ux * vx + uy * vy);
  const theta = angle(1, 0, (x1p - cxp) / rx, (y1p - cyp) / ry);
  let delta = angle((x1p - cxp) / rx, (y1p - cyp) / ry, (-x1p - cxp) / rx, (-y1p - cyp) / ry);
  if (!sweep && delta > 0) delta -= Math.PI * 2;
  if (sweep && delta < 0) delta += Math.PI * 2;
  const n = segmentsFor(Math.max(rx, ry), delta, tolerance);
  const out: Point[] = [];
  for (let i = 1; i <= n; i++) {
    const a = theta + (delta * i) / n;
    const ex = rx * Math.cos(a);
    const ey = ry * Math.sin(a);
    out.push({ x: cos * ex - sin * ey + cx, y: sin * ex + cos * ey + cy });
  }
  out[out.length - 1] = { x: x2, y: y2 };
  return out;
}

const near = (a: Point, b: Point): boolean => Math.abs(a.x - b.x) < 1e-9 && Math.abs(a.y - b.y) < 1e-9;

// --- measuring ----------------------------------------------------------------------------------

/** Twice the signed area; positive when the points run clockwise on screen (y down). */
export function signedArea(points: readonly Point[]): number {
  let a = 0;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    a += (points[j]!.x - points[i]!.x) * (points[j]!.y + points[i]!.y);
  }
  return a / 2;
}

export function polylineLength(points: readonly Point[], closed = false): number {
  let total = 0;
  for (let i = 1; i < points.length; i++) total += Math.hypot(points[i]!.x - points[i - 1]!.x, points[i]!.y - points[i - 1]!.y);
  if (closed && points.length > 1) {
    const a = points[points.length - 1]!;
    total += Math.hypot(points[0]!.x - a.x, points[0]!.y - a.y);
  }
  return total;
}

/** Even-odd: inside one ring and not inside a ring within it. */
export function pointInPolygon(p: Point, polygon: readonly Point[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i]!;
    const b = polygon[j]!;
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

/** Even-odd over several rings at once, so a hole is a hole. */
export function pointInShape(p: Point, rings: readonly (readonly Point[])[]): boolean {
  let inside = false;
  for (const ring of rings) if (pointInPolygon(p, ring)) inside = !inside;
  return inside;
}

// --- trimming and dashing -------------------------------------------------------------------------

/** The part of a polyline between `from` and `to`, as fractions of its length. */
export function trimPolyline(points: readonly Point[], closed: boolean, from: number, to: number): Point[] {
  const ring = closed && points.length > 1 ? [...points, points[0]!] : [...points];
  const total = polylineLength(ring);
  const a = Math.max(0, Math.min(1, from)) * total;
  const b = Math.max(0, Math.min(1, to)) * total;
  if (b <= a || ring.length < 2) return [];
  return slice(ring, a, b);
}

/** The run of `ring` from length `a` to length `b`. */
function slice(ring: readonly Point[], a: number, b: number): Point[] {
  const out: Point[] = [];
  let walked = 0;
  for (let i = 1; i < ring.length; i++) {
    const p = ring[i - 1]!;
    const q = ring[i]!;
    const len = Math.hypot(q.x - p.x, q.y - p.y);
    const s0 = walked;
    const s1 = walked + len;
    walked = s1;
    if (s1 < a || len === 0) continue;
    if (s0 > b) break;
    const at = (s: number): Point => {
      const k = (s - s0) / len;
      return { x: p.x + (q.x - p.x) * k, y: p.y + (q.y - p.y) * k };
    };
    if (out.length === 0) out.push(at(Math.max(a, s0)));
    out.push(s1 <= b ? q : at(b));
    if (s1 >= b) break;
  }
  return out;
}

/**
 * A polyline cut into dashes: `pattern` alternates on and off lengths, starting `offset` along it.
 * An odd pattern repeats, as SVG's does, so `[6]` is six on, six off.
 */
export function dashPolyline(points: readonly Point[], closed: boolean, pattern: readonly number[], offset = 0): Point[][] {
  const lengths = pattern.filter((n) => n > 0);
  if (lengths.length === 0) return [closed ? [...points, points[0]!] : [...points]];
  const dash = lengths.length % 2 === 1 ? [...lengths, ...lengths] : lengths;
  const period = dash.reduce((s, n) => s + n, 0);
  const ring = closed && points.length > 1 ? [...points, points[0]!] : [...points];
  const total = polylineLength(ring);
  const out: Point[][] = [];
  let s = -(((offset % period) + period) % period);
  let k = 0;
  while (s < total) {
    const len = dash[k % dash.length]!;
    if (k % 2 === 0) {
      const a = Math.max(0, s);
      const b = Math.min(total, s + len);
      if (b > a) out.push(slice(ring, a, b));
    }
    s += len;
    k++;
    if (k > 100000) break;
  }
  return out;
}

// --- triangles ---------------------------------------------------------------------------------

/**
 * A triangle as the renderer takes it, with which of its edges lie on the shape's boundary.
 * Only those are antialiased: an edge shared with a neighboring triangle is drawn hard, so the
 * two meet exactly and no hairline seam shows where a shape was cut up.
 */
export interface Triangle {
  a: Point;
  b: Point;
  c: Point;
  /** Bit 0: a-b is on the boundary; bit 1: b-c; bit 2: c-a. */
  edges: number;
}

/**
 * Ear-clip `outer` (with any `holes` cut into it) into triangles. Either winding; collinear and
 * repeated points are fine. A self-crossing ring is filled as best ear clipping can, which is not
 * what even-odd would say - use separate rings for that.
 */
export function triangulate(outer: readonly Point[], holes: readonly (readonly Point[])[] = []): Triangle[] {
  let ring = clean(outer);
  if (ring.length < 3) return [];
  if (signedArea(ring) < 0) ring.reverse();
  /** Edges made by joining a hole to the outline; on no boundary, so never antialiased. */
  const bridges = new Set<string>();
  const key = (p: Point, q: Point) => {
    const a = `${p.x},${p.y}`;
    const b = `${q.x},${q.y}`;
    return a < b ? `${a}|${b}` : `${b}|${a}`;
  };
  const sorted = holes
    .map(clean)
    .filter((h) => h.length >= 3)
    .map((h) => (signedArea(h) > 0 ? [...h].reverse() : h))
    .sort((p, q) => Math.max(...q.map((v) => v.x)) - Math.max(...p.map((v) => v.x)));
  for (const hole of sorted) ring = bridge(ring, hole, bridges, key);

  const pts = ring;
  const n = pts.length;
  const index = Array.from({ length: n }, (_, i) => i);
  const boundary = (i: number, j: number): boolean => {
    const d = Math.abs(i - j);
    if (d !== 1 && d !== n - 1) return false;
    return !bridges.has(key(pts[i]!, pts[j]!));
  };
  const out: Triangle[] = [];
  let guard = 0;
  while (index.length > 3 && guard++ < n * n) {
    let clipped = false;
    for (let k = 0; k < index.length; k++) {
      const i0 = index[(k + index.length - 1) % index.length]!;
      const i1 = index[k]!;
      const i2 = index[(k + 1) % index.length]!;
      const a = pts[i0]!;
      const b = pts[i1]!;
      const c = pts[i2]!;
      if (cross(a, b, c) <= 1e-12) continue;
      let blocked = false;
      for (const j of index) {
        if (j === i0 || j === i1 || j === i2) continue;
        const p = pts[j]!;
        if ((p.x === a.x && p.y === a.y) || (p.x === b.x && p.y === b.y) || (p.x === c.x && p.y === c.y)) continue;
        if (inTriangle(p, a, b, c)) {
          blocked = true;
          break;
        }
      }
      if (blocked) continue;
      out.push({ a, b, c, edges: (boundary(i0, i1) ? 1 : 0) | (boundary(i1, i2) ? 2 : 0) | (boundary(i2, i0) ? 4 : 0) });
      index.splice(k, 1);
      clipped = true;
      break;
    }
    // Nothing clippable is a degenerate or self-crossing remainder; drop its flattest vertex.
    if (!clipped) {
      let worst = 0;
      let flattest = Infinity;
      for (let k = 0; k < index.length; k++) {
        const a = pts[index[(k + index.length - 1) % index.length]!]!;
        const b = pts[index[k]!]!;
        const c = pts[index[(k + 1) % index.length]!]!;
        const area = Math.abs(cross(a, b, c));
        if (area < flattest) {
          flattest = area;
          worst = k;
        }
      }
      index.splice(worst, 1);
    }
  }
  if (index.length === 3) {
    const [i0, i1, i2] = index as [number, number, number];
    if (Math.abs(cross(pts[i0]!, pts[i1]!, pts[i2]!)) > 1e-12) {
      out.push({
        a: pts[i0]!,
        b: pts[i1]!,
        c: pts[i2]!,
        edges: (boundary(i0, i1) ? 1 : 0) | (boundary(i1, i2) ? 2 : 0) | (boundary(i2, i0) ? 4 : 0),
      });
    }
  }
  return out;
}

/**
 * Sort rings into outlines and the holes in them by nesting: a ring inside an odd number of others
 * is a hole in the innermost of those. This is even-odd, which is what a path with a hole in it
 * means to anybody who drew it.
 */
export function fillRings(rings: readonly (readonly Point[])[]): Triangle[] {
  const usable = rings.filter((r) => r.length >= 3);
  const depth = usable.map((r, i) => usable.filter((o, j) => j !== i && pointInPolygon(r[0]!, o)).length);
  const out: Triangle[] = [];
  usable.forEach((ring, i) => {
    if (depth[i]! % 2 === 1) return;
    const holes = usable.filter((h, j) => depth[j] === depth[i]! + 1 && pointInPolygon(h[0]!, ring));
    out.push(...triangulate(ring, holes));
  });
  return out;
}

function clean(points: readonly Point[]): Point[] {
  const out: Point[] = [];
  for (const p of points) {
    const last = out[out.length - 1];
    if (!last || Math.abs(last.x - p.x) > 1e-9 || Math.abs(last.y - p.y) > 1e-9) out.push(p);
  }
  while (out.length > 1 && near(out[0]!, out[out.length - 1]!)) out.pop();
  return out;
}

/** Positive when a-b-c turns clockwise on screen, which is the winding `triangulate` clips. */
function cross(a: Point, b: Point, c: Point): number {
  return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}

function inTriangle(p: Point, a: Point, b: Point, c: Point): boolean {
  return cross(a, b, p) >= 0 && cross(b, c, p) >= 0 && cross(c, a, p) >= 0;
}

/** Join `hole` into `ring` along a seam from its rightmost point, so one ring can be clipped. */
function bridge(ring: Point[], hole: Point[], bridges: Set<string>, key: (p: Point, q: Point) => string): Point[] {
  let h = 0;
  for (let i = 1; i < hole.length; i++) if (hole[i]!.x > hole[h]!.x) h = i;
  const m = hole[h]!;
  // The nearest outline edge a ray to the right meets, and the end of it that can see m.
  let best = -1;
  let bestX = Infinity;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i]!;
    const b = ring[(i + 1) % ring.length]!;
    if ((a.y > m.y) === (b.y > m.y) && a.y !== m.y && b.y !== m.y) continue;
    if (a.y === b.y) continue;
    const x = a.x + ((m.y - a.y) * (b.x - a.x)) / (b.y - a.y);
    if (x >= m.x && x < bestX) {
      bestX = x;
      best = a.x > b.x ? i : (i + 1) % ring.length;
    }
  }
  if (best < 0) {
    best = 0;
    for (let i = 1; i < ring.length; i++) {
      if (Math.hypot(ring[i]!.x - m.x, ring[i]!.y - m.y) < Math.hypot(ring[best]!.x - m.x, ring[best]!.y - m.y)) best = i;
    }
  }
  const o = ring[best]!;
  bridges.add(key(o, m));
  const around = [...hole.slice(h), ...hole.slice(0, h), m];
  return [...ring.slice(0, best + 1), ...around, o, ...ring.slice(best + 1)];
}

/**
 * A stroke as triangles: a strip down the line, its corners mitred, so a translucent line is one
 * coat of paint all the way along - stroking with a capsule per segment doubles the alpha at every
 * joint. A corner sharper than `miterLimit` (the mitre's length over the stroke's half-width) is
 * bevelled instead: its outer point is cut off, which keeps a star's tip from throwing a spike.
 */
export function strokeTriangles(points: readonly Point[], closed: boolean, width: number, miterLimit = 4): Triangle[] {
  const pts = clean(points);
  if (closed && pts.length > 2 && near(pts[0]!, pts[pts.length - 1]!)) pts.pop();
  const n = pts.length;
  if (n < 2 || !(width > 0)) return [];
  const half = width / 2;
  const segs = closed ? n : n - 1;
  const normal = (i: number): Point => {
    const a = pts[((i % n) + n) % n]!;
    const b = pts[(((i + 1) % n) + n) % n]!;
    const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    return { x: -(b.y - a.y) / len, y: (b.x - a.x) / len };
  };
  const off = (p: Point, nx: number, ny: number, k: number): Point => ({ x: p.x + nx * k, y: p.y + ny * k });
  /** Where each vertex's left and right edges are, for the segment arriving and the one leaving. */
  const arrive: { l: Point; r: Point }[] = [];
  const leave: { l: Point; r: Point }[] = [];
  const out: Triangle[] = [];
  for (let i = 0; i < n; i++) {
    const p = pts[i]!;
    const ends = !closed && (i === 0 || i === n - 1);
    if (ends) {
      const nn = normal(i === 0 ? 0 : n - 2);
      const pair = { l: off(p, nn.x, nn.y, half), r: off(p, nn.x, nn.y, -half) };
      arrive.push(pair);
      leave.push(pair);
      continue;
    }
    const n0 = normal(i - 1);
    const n1 = normal(i);
    let mx = n0.x + n1.x;
    let my = n0.y + n1.y;
    const len = Math.hypot(mx, my);
    const cos = len < 1e-6 ? 0 : (mx / len) * n1.x + (my / len) * n1.y;
    const scale = cos > 1e-6 ? 1 / cos : Infinity;
    if (scale <= miterLimit) {
      mx /= len;
      my /= len;
      const pair = { l: off(p, mx, my, half * scale), r: off(p, mx, my, -half * scale) };
      arrive.push(pair);
      leave.push(pair);
      continue;
    }
    // Bevel: each segment keeps its own square end here, and a triangle fills the outside of the turn.
    const a = { l: off(p, n0.x, n0.y, half), r: off(p, n0.x, n0.y, -half) };
    const b = { l: off(p, n1.x, n1.y, half), r: off(p, n1.x, n1.y, -half) };
    arrive.push(a);
    leave.push(b);
    const turn = n0.x * n1.y - n0.y * n1.x;
    const [o0, o1] = turn > 0 ? [a.r, b.r] : [a.l, b.l];
    out.push({ a: p, b: o0, c: o1, edges: 2 });
  }
  for (let s = 0; s < segs; s++) {
    const i = s;
    const j = (s + 1) % n;
    const startCap = !closed && s === 0;
    const endCap = !closed && s === segs - 1;
    const A = leave[i]!;
    const B = arrive[j]!;
    // (L_i, R_i, R_j): L_i-R_i is the start cap; R_i-R_j the right side; R_j-L_i the inner diagonal.
    out.push({ a: A.l, b: A.r, c: B.r, edges: (startCap ? 1 : 0) | 2 });
    // (L_i, R_j, L_j): R_j-L_j is the end cap; L_j-L_i the left side.
    out.push({ a: A.l, b: B.r, c: B.l, edges: (endCap ? 2 : 0) | 4 });
  }
  return out;
}

// --- silhouettes ----------------------------------------------------------------------------------

/** The shapes a control's `shape` can name. Anything else is path data in a 100 by 100 box. */
export const SILHOUETTES = ["circle", "pill", "hexagon", "octagon", "diamond", "chevron", "tab"] as const;

/**
 * A control's outline at `w` by `h`, as rings in its own pixels. A preset keeps its proportions
 * sensible at any size - a pill's ends stay round however wide it is; path data is stretched from
 * its 100 by 100 box, which is what lets one string describe a shape for every size of button.
 */
export function silhouetteOf(spec: string, w: number, h: number): Point[][] {
  if (w <= 0 || h <= 0) return [];
  const s = spec.trim();
  switch (s) {
    case "circle":
      return [ellipsePoints(w / 2, h / 2, w / 2, h / 2, 0.5)];
    case "pill":
      return [rectPoints(0, 0, w, h, Math.min(w, h) / 2, Math.min(w, h) / 2, 0.5)];
    case "hexagon": {
      const k = Math.min(w * 0.25, (h / 2) * 0.577);
      return [[{ x: k, y: 0 }, { x: w - k, y: 0 }, { x: w, y: h / 2 }, { x: w - k, y: h }, { x: k, y: h }, { x: 0, y: h / 2 }]];
    }
    case "octagon": {
      const c = Math.min(w, h) * 0.29;
      return [[
        { x: c, y: 0 }, { x: w - c, y: 0 }, { x: w, y: c }, { x: w, y: h - c },
        { x: w - c, y: h }, { x: c, y: h }, { x: 0, y: h - c }, { x: 0, y: c },
      ]];
    }
    case "diamond":
      return [[{ x: w / 2, y: 0 }, { x: w, y: h / 2 }, { x: w / 2, y: h }, { x: 0, y: h / 2 }]];
    case "chevron": {
      const k = Math.min(w * 0.3, h / 2);
      return [[{ x: 0, y: 0 }, { x: w - k, y: 0 }, { x: w, y: h / 2 }, { x: w - k, y: h }, { x: 0, y: h }, { x: k, y: h / 2 }]];
    }
    case "tab": {
      const k = Math.min(w * 0.2, h * 0.6);
      return [[{ x: k, y: 0 }, { x: w - k, y: 0 }, { x: w, y: h }, { x: 0, y: h }]];
    }
  }
  const m: Matrix = [w / 100, 0, 0, h / 100, 0, 0];
  return flattenPath(s, 0.5)
    .filter((p) => p.closed || p.points.length > 2)
    .map((p) => applyAll(m, p.points));
}
